// react_frontend/src/Components/Modules/Billing/BillingList.js
import React, { useEffect, useState, useMemo, useContext } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Button,
  Input,
  Badge,
  Loader,
  Message,
  Modal,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";
import {
  FaFileInvoice,
  FaUser,
  FaPhone,
  FaClipboard,
  FaDollarSign,
  FaMoneyBillWave,
  FaEye,
  FaTrashAlt,
  FaPlus,
  FaSearch,
  FaPrint
} from "react-icons/fa";
import moment from "moment";

const BillingList = () => {
  const {
    billings,
    loadingBillings,
    startBillingPolling,
    stopBillingPolling,
    removeBillingFromList,
    company,
  } = useContext(MotherContext);

  const [search, setSearch] = useState("");
  const [modalData, setModalData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState(null);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: company.currency || "BDT",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const isAdminOrAccount = ["1", "2"].includes(localStorage.getItem("who_am_i"));

  const navigate = useNavigate();

  // ✅ Print — normalize shape so PrintReceipt.js can read it
  const handlePrint = (billing) => {
    if (!billing) return;
    const normalized = {
      ...billing,
      billing_items: billing.billing_items || billing.billingItems || [],
      customer_name: billing.customer?.name || billing.customer_name || "Walk-in Customer",
      customer_phone: billing.customer?.phone || billing.customer_phone || "",
    };
    navigate("/print-receipt", { state: { billing: normalized, company } });
  };

  // ✅ Polling start/stop
  useEffect(() => {
    startBillingPolling();
    return () => stopBillingPolling();
  }, [startBillingPolling, stopBillingPolling]);

  // ✅ Filtered Data
  const filteredData = useMemo(() => {
    if (!search) return billings;
    const lowerSearch = search.toLowerCase();
    return billings.filter((item) => {
      const orderNumber = String(item.order_number || "").toLowerCase();
      const customerName = String(item.customer?.name || item.customer_name || "").toLowerCase();
      const customerPhone = String(item.customer?.phone || item.customer_phone || "").toLowerCase();
      return orderNumber.includes(lowerSearch) || customerName.includes(lowerSearch) || customerPhone.includes(lowerSearch);
    });
  }, [billings, search]);

  // ✅ Handle View
  const handleView = (billing) => {
    if (!billing) return;
    setModalData(billing.billingItems || []);
    setSelectedBilling(billing);
    setModalOpen(true);
  };

  // ✅ Handle Delete
  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this billing record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.delete(`/billings/${row.id}`);
        removeBillingFromList(row.id);
        toaster.push(
          <Message type="success" duration={3000}>
            Billing record deleted successfully!
          </Message>,
          { placement: "topEnd" }
        );
      } catch (err) {
        toaster.push(
          <Message type="error" duration={5000}>
            Failed to delete billing.
          </Message>,
          { placement: "topEnd" }
        );
      }
    }
  };

  // ✅ Columns (same as yours)
  const columns = [
    { name: "SL", cell: (row, index) => <strong>{index + 1}</strong>, width: "5%", center: true },
    {
      name: "Billing No",
      cell: (row) => (
        <Badge color="blue" style={{ fontSize: "0.8em" }}>
          <FaClipboard /> {row.order_number}
        </Badge>
      ),
      width: "8%",
    },
    {
      name: "Bill Date",
      cell: (row) => (
        <small className="text-dark">
          {moment(row.billing_date).format("MMM DD, YYYY")}
        </small>
      ),
      width: "8%",
    },
    {
      name: "Customer",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaUser className="text-primary" />
          <span>{row.customer?.name || row.customer_name || "N/A"} / {row.customer?.phone || row.customer_phone || "N/A"}</span>
        </div>
      ),
      width: "10%",
    },
    {
      name: "Point Used",
      cell: (row) => (
        <Badge color={row.point_used > 0 ? "green" : "blue"}>
          {row.point_used || 0}
        </Badge>
      ),
      width: "9%",
    },
    {
      name: "Discount",
      cell: (row) => (
        <Badge color="orange" style={{ fontSize: "0.8em" }}>
          ৳{Number(row.total_discount).toLocaleString()}
        </Badge>
      ),
      width: "8%",
    },
    {
      name: "Total Amount",
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <FaDollarSign className="text-success" />
          <strong>{Number(row.total_amount_to_be_paid).toLocaleString()}</strong>
        </div>
      ),
      width: "10%",
    },
    {
      name: "Created By",
      cell: (row) => <div className="fw-medium">{row.created_by || "System"}</div>,
      width: "10%",
    },
    {
      name: "Payment",
      cell: (row) => (
        <Badge
          color={
            row.payment_method?.toLowerCase() === "cash"
              ? "green"
              : row.payment_method?.toLowerCase() === "card"
                ? "blue"
                : "yellow"
          }
          style={{ fontSize: "0.8em" }}
        >
          <FaMoneyBillWave /> {row.payment_method || "N/A"}
        </Badge>
      ),
      width: "7%",
    },
    {
      name: "Created at",
      cell: (row) => (
        <div className="small">
          <div> {moment(row.created_at).format("MMM DD, h:mm A")}</div>
          {/* <div><strong>Updated:</strong> {row.updated_at === row.created_at ? "—" : moment(row.updated_at).format("MMM DD, h:mm A")}</div> */}
        </div>
      ),
      width: "9%",
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex gap-1 justify-content-center">
          <Button size="sm" color="yellow" startIcon={<FaEye />} onClick={() => handleView(row)} title="View details">View</Button>
          <Button size="sm" color="green" startIcon={<FaPrint />} onClick={() => handlePrint(row)} title="Print invoice">Print</Button>
          {isAdminOrAccount && (
            <Button size="sm" color="red" startIcon={<FaTrashAlt />} onClick={() => handleDelete(row)} title="Delete billing">Delete</Button>
          )}
        </div>
      ),
      width: "15%",
      center: true,
    },
  ];

  const customStyles = {
    head: { style: { fontSize: "14px", fontWeight: "bold", backgroundColor: "#f8f9fa" } },
    rows: { style: { fontSize: "14px", minHeight: "70px" } },
    cells: { style: { padding: "12px 8px" } },
  };

  // ✅ VAT Calculation
  const totalPrice = modalData.reduce((sum, item) => sum + Number(item.sub_total), 0);
  const discount = Number(selectedBilling?.total_discount || 0);
  const points = Number(selectedBilling?.point_used || 0);
  const netPrice = Number(selectedBilling?.total_amount_to_be_paid || 0);
  const vat = netPrice - (totalPrice - (discount + points));

  return (
    <>
      <Breadcrumb title="Billing List" />
      <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <h5 className="text-dark mb-0">
          <FaFileInvoice className="me-2" /> Billing Records
        </h5>
        <div className="d-flex gap-2 w-100 w-md-auto align-items-center">
          <Input
            placeholder="Search by order, customer..."
            value={search}
            onChange={setSearch}
            prefix={<FaSearch />}
            style={{ width: 300 }}
            size="md"
          />
          <Link to="/add_billing" style={{ textDecoration: "none" }}>
            <Button appearance="primary" startIcon={<FaPlus />} className="d-flex align-items-center custom-bg">
              Add Billing
            </Button>
          </Link>
        </div>
      </div>

      <DataTable
        keyField="id"
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[100, 250, 500, 1000]}
        progressPending={loadingBillings}
        progressComponent={
          <div className="d-flex justify-content-center my-5">
            <Loader size="md" /> <span className="ms-2">Loading billings...</span>
          </div>
        }
        noDataComponent={
          <div className="text-center my-5 text-muted">
            <FaFileInvoice size={48} className="mb-3 text-secondary" />
            <h5>No billings found</h5>
            <p>Try adding a new billing or check back later.</p>
          </div>
        }
        highlightOnHover
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        customStyles={customStyles}
        striped
      />

      {/* View Modal — same as yours */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="lg">
        <Modal.Header className="border-bottom pb-2">
          <Modal.Title><FaClipboard className="me-2" /> Billing Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="w-100 view-modal-body">
          {modalData.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FaFileInvoice size={48} className="mb-3 opacity-50" />
              <p>No items found in this billing.</p>
            </div>
          ) : (
            <div>
              <div className="row mb-4 border-bottom pb-3 w-100">
                <div className="col-6">
                  <h6 className="fw-bold">Customer</h6>
                  <p><strong>Name:</strong> {selectedBilling.customer_name || "N/A"}</p>
                  <p><strong>Phone:</strong> {selectedBilling.customer_phone || "N/A"}</p>
                </div>
                <div className="col-6 d-flex flex-column align-items-end">
                  <h6 className="fw-bold">Billing Info</h6>
                  <p><strong>Order No:</strong> {selectedBilling.order_number || "N/A"}</p>
                  <p><strong>Date:</strong> {moment(selectedBilling.billing_date).format("MMM DD, YYYY")}</p>
                  <p><strong>Created by:</strong> {selectedBilling.created_by || "System"}</p>
                </div>
              </div>

              <div className="table-responsive mb-4">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "8%" }}>SL</th>
                      <th>Item Name</th>
                      <th style={{ width: "15%" }}>Unit Price</th>
                      <th style={{ width: "10%" }}>Qty</th>
                      <th style={{ width: "15%" }}>Sub Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.map((item, i) => (
                      <tr key={item.id || i}>
                        <td><strong>{i + 1}</strong></td>
                        <td>{item.item?.item_name || "N/A"}</td>
                        <td>{formatCurrency(item.unit_price)}</td>
                        <td>{item.quantity}</td>
                        <td><strong>{formatCurrency(item.sub_total)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="row g-3">
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center">
                    <small className="text-muted">Total Items</small>
                    <div className="h5 fw-bold">{modalData.length}</div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center">
                    <small className="text-muted">Total</small>
                    <div className="h5 fw-bold text-success">
                      {formatCurrency(totalPrice)}
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center">
                    <small className="text-muted">VAT</small>
                    <div className="h5 fw-bold text-primary">{formatCurrency(vat)}</div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center">
                    <small className="text-muted">Discount</small>
                    <div className="h5 fw-bold text-danger">
                      {formatCurrency(discount)}
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center">
                    <small className="text-muted">Points Used</small>
                    <div className="h5 fw-bold text-warning">{points}</div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center">
                    <small className="text-muted">Payment</small>
                    <div className="h5 fw-bold">
                      <Badge
                        color={
                          selectedBilling.payment_method?.toLowerCase() === "cash"
                            ? "green"
                            : selectedBilling.payment_method?.toLowerCase() === "card"
                              ? "blue"
                              : "yellow"
                        }
                        style={{ fontSize: "0.9em" }}
                      >
                        {selectedBilling.payment_method || "N/A"}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-12">
                  <div className="p-3 bg-success text-white border rounded text-center">
                    <small>Net Payable</small>
                    <div className="h4 fw-bold">
                      {formatCurrency(netPrice)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            className="custom-bg me-2 mt-4"
            startIcon={<FaPrint />}
            onClick={() => handlePrint(selectedBilling)}
          >
            Print Invoice
          </Button>
          <Button className="border mt-4" onClick={() => setModalOpen(false)} appearance="subtle">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BillingList;